import _ from 'lodash';
import React, { ComponentProps, memo, useCallback, useEffect, useMemo } from 'react';

import 'app/components/Viewer/components/RightPanel/FilterParametrization.scss';
import { FormattedMessage, useIntl } from 'react-intl';
import { Accordion, Button, Checkbox, Icon, Input, Label } from 'semantic-ui-react';
import ConfirmWithIntl from 'app/components/ConfirmWithIntl';
import { FinetunedOptions, FullAlgorithmOptions } from 'app/interfaces/IImageProcessor';
import preventDefault from 'app/utils/preventDefault';
import ImageHistogramDisplay from 'app/components/ImageHistogramDisplay';
import { DisplayableImageData } from 'app/interfaces/Image';

export const DEFAULT_FINETUNING_PARAMETERS = {
  gamma: 1,
};

function rangeToRangeMapping(value: number, from: [number, number], to: [number, number]) {
  return ((value - from[0]) * (to[1] - to[0])) / (from[1] - from[0]) + to[0];
}

function multiRangeMapping(value: number, from: number[], to: number[]) {
  const rangeIndex = _.findIndex(from, (fromValue, index) => {
    return (
      from[index + 1] === undefined ||
      (value >= fromValue && value <= from[index + 1]) ||
      (value <= fromValue && value >= from[index + 1])
    );
  });
  return rangeToRangeMapping(
    value,
    [from[rangeIndex], from[rangeIndex + 1]],
    [to[rangeIndex], to[rangeIndex + 1]]
  );
}

function makeValueMapperWithReverse(from: number[], to: number[]) {
  return {
    toDisplay: (value: number) => +Math.round(multiRangeMapping(value, from, to)),
    toValue: (value: number) => multiRangeMapping(value, to, from),
  };
}

const FILTER_PARAMETERS_MAPPINGS = {
  intensities: makeValueMapperWithReverse([0.3, 1], [20, 0]),
  denoise_intensities: makeValueMapperWithReverse([0, 1], [0, 100]),
  black_relative_steepness: makeValueMapperWithReverse([1, 4], [0, 20]),
  white_relative_steepness: makeValueMapperWithReverse([1, 4], [0, 20]),
  general_steepness: makeValueMapperWithReverse([0.1, 4], [1, 20]),
  centering_quantile: makeValueMapperWithReverse([0.1, 0.9], [10, -10]),
  lowLatitudeCompression: makeValueMapperWithReverse([0.3, 1, 2.5], [-10, 0, 10]),
} as const;

const FILTERS_PARAMETERS = {
  // sharpening: {
  //   // radius: { min: 0, max: 30, step: 0.1 },
  //   // strength: { min: 0, max: 10, step: 0.1 },
  //   // threshold: { min: 0, max: 1, step: 0.001 },
  // },
  intensities: { min: 0, max: 20, step: 1 },
  denoise_intensities: { min: 0, max: 100, step: 1 },
  black_relative_steepness: { min: 0, max: 20, step: 1 },
  white_relative_steepness: { min: 0, max: 20, step: 1 },
  general_steepness: { min: 1, max: 20, step: 1 },
  centering_quantile: { min: -10, max: 10, step: 1 },
  need_log_rescale: { min: 0, max: 1, step: 1 },
  late_log_rescale: { min: 0, max: 1, step: 1 },
  gamma: { min: 0.1, max: 2, step: 0.1 },
  lowLatitudeCompression: { min: -10, max: 10, step: 1 },
} as const;

function getParameterMapping(name: string) {
  const baseKey = name.split('|')[0].replace(/\[.*\]/, '');
  return FILTER_PARAMETERS_MAPPINGS[baseKey as keyof typeof FILTER_PARAMETERS_MAPPINGS];
}

export type FilterDescription = {
  name: string;
  id?: string;
  parameters: FullAlgorithmOptions;
};

type FilterParametrizationProps = {
  referenceFilter: FilterDescription['parameters'];
  filter: FilterDescription;
  imageForHist?: { image: DisplayableImageData; crop?: [number, number, number, number] };
  onChange: (filter: FilterDescription) => void;
  onConfirm: (filter: FilterDescription) => void;
  onCancel: (filter: FilterDescription) => void;
  onDelete: (filter: FilterDescription) => void;
  isFineTunedProcessingOngoing?: boolean;
};

function ValueSlider({
  value,
  reference,
  min,
  max,
  step,
  name,
  label,
  onChange,
  disabled,
}: {
  value: number;
  reference: number;
  name: string;
  label?: React.ReactNode | string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
} & React.ComponentProps<'input'>) {
  const intl = useIntl();
  const ratio = (value - +min) / (+max - +min);
  return (
    <label className="value-slider-group">
      {label ?? <FormattedMessage id={`tools.processing.finetuning.filter.${name}.label`} />}
      <div className="input-with-slider">
        <input
          className="slider"
          style={{ backgroundSize: `${ratio * 100}% 100%` }}
          type="range"
          min={min.toString()}
          max={max.toString()}
          step={step.toString()}
          value={+value.toFixed(2)}
          name={name}
          onChange={onChange}
          disabled={disabled}
        />
        <input
          className="number-input"
          min={min.toString()}
          max={max.toString()}
          step={step.toString()}
          type="number"
          value={+value.toFixed(2)}
          name={name}
          onChange={onChange}
          disabled={disabled}
        />
        <input
          className="number-input"
          type="text"
          value={+reference.toFixed(2)}
          title={intl.formatMessage({ id: `tools.processing.finetuning.reference_value` })}
          disabled
        />
      </div>
    </label>
  );
}

export default memo(function FilterParametrization({
  filter,
  referenceFilter,
  imageForHist,
  onChange,
  onConfirm,
  onCancel,
  onDelete,
  isFineTunedProcessingOngoing,
}: FilterParametrizationProps) {
  const intl = useIntl();

  const [isFilterDeletionOpen, setIsFilterDeletionOpen] = React.useState(false);

  useEffect(() => {
    const parametersWithDefaults = _.merge(
      {},
      {
        name: intl.formatMessage({ id: 'tools.processing.finetuning.filter_name.default' }),
        parameters: DEFAULT_FINETUNING_PARAMETERS,
      },
      filter
    );
    if (!_.isEqual(filter?.parameters, parametersWithDefaults)) {
      onChange(parametersWithDefaults);
    }
  }, [filter, intl]);

  const onBooleanValueChange: ComponentProps<typeof Checkbox>['onChange'] = useCallback(
    (e, { checked, name }) => {
      const newFilter = _.cloneDeep(filter);
      _.set(newFilter.parameters, name, checked);
      onChange(newFilter);
      preventDefault(e);
    },
    [onChange, filter]
  );

  const onFloatValueChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      const newFilter = _.cloneDeep(filter);
      const mapping = getParameterMapping(name);
      const realValue = mapping ? mapping.toValue(parseFloat(value)) : parseFloat(value);
      _.set(newFilter.parameters, name, realValue);
      onChange(newFilter);
      preventDefault(e);
    },
    [onChange, filter]
  );

  const onGroupFloatValueChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value, min, max } = e.target;
      const paths = name.split('|');
      const newFilter = _.cloneDeep(filter);
      const floatValue = parseFloat(value);
      const mapping = getParameterMapping(paths[0]);

      const currentDisplayValues = _.map(paths, (path) => {
        const realValue = _.get(newFilter.parameters, path);
        return mapping?.toDisplay?.(realValue) ?? realValue;
      });
      const currentDisplayMean = _.mean(currentDisplayValues) || 0;
      const offset = floatValue - currentDisplayMean;

      paths.forEach((path, index) => {
        const newDisplayValue = Math.max(
          Math.min(currentDisplayValues[index] + offset, parseFloat(max)),
          parseFloat(min)
        );
        const newRealValue = mapping?.toValue?.(newDisplayValue) ?? newDisplayValue;
        _.set(newFilter.parameters, path, newRealValue);
      });
      onChange(newFilter);
      preventDefault(e);
    },
    [onChange, filter]
  );

  const INTENSITIES_CHUNK_SIZE = 3;

  const buildGroupedPanel = (parameterKey: string, sectionNames: string[], chunkSize: number) =>
    _.map(sectionNames, (groupName, chunkIndex) => {
      const parametersChunk: number[] = _.get(filter.parameters, parameterKey, []).slice(
        chunkSize * chunkIndex + 0,
        chunkSize * chunkIndex + chunkSize
      );
      const referenceChunk: number[] = _.get(referenceFilter, parameterKey, []).slice(
        chunkSize * chunkIndex + 0,
        chunkSize * chunkIndex + chunkSize
      );
      const mapping =
        FILTER_PARAMETERS_MAPPINGS[parameterKey as keyof typeof FILTER_PARAMETERS_MAPPINGS];
      const displayChunk = parametersChunk.map((v) => mapping?.toDisplay?.(v) ?? v);
      const meanDisplayValue =
        mapping?.toDisplay?.(_.mean(parametersChunk)) ?? _.mean(parametersChunk);
      const meanReferenceDisplayValue =
        mapping?.toDisplay?.(_.mean(referenceChunk)) ?? _.mean(referenceChunk);
      const displayReferenceChunk = referenceChunk.map((v) => mapping?.toDisplay?.(v) ?? v);
      const chunkRepr = _.map(
        parametersChunk,
        (v, i) => `${parameterKey}[${chunkSize * chunkIndex + i}]`
      ).join('|');
      return {
        key: `${parameterKey}.${chunkIndex}`,
        title: (
          <Accordion.Title>
            <i className="icon dropdown-state" />
            <div className="parameter-group-head" onClick={preventDefault}>
              <ValueSlider
                value={meanDisplayValue}
                reference={meanReferenceDisplayValue}
                name={chunkRepr}
                label={
                  <FormattedMessage
                    id={`tools.processing.finetuning.filter.${parameterKey}.${groupName}.label`}
                  />
                }
                {..._.get(FILTERS_PARAMETERS, parameterKey)}
                onChange={onGroupFloatValueChange}
                disabled={isFineTunedProcessingOngoing}
              />
            </div>
          </Accordion.Title>
        ),
        content: (
          <Accordion.Content className="parameter-unit">
            {displayChunk.map((value, i) => (
              <ValueSlider
                key={i}
                value={value}
                reference={displayReferenceChunk[i]}
                name={`${parameterKey}[${chunkSize * chunkIndex + i}]`}
                label={
                  <FormattedMessage
                    id={`tools.processing.finetuning.filter.${parameterKey}.${groupName}.level.label`}
                    values={{ level: i + 1 }}
                  />
                }
                {..._.get(FILTERS_PARAMETERS, parameterKey)}
                onChange={onFloatValueChange}
                disabled={isFineTunedProcessingOngoing}
              />
            ))}
          </Accordion.Content>
        ),
      };
    });

  const intensitiesPanels = useMemo(
    () => buildGroupedPanel('intensities', ['high_res', 'medium_res'], INTENSITIES_CHUNK_SIZE),
    [filter.parameters?.intensities, onFloatValueChange, onGroupFloatValueChange]
  );
  // const denoisePanels = useMemo(
  //   () =>
  //     buildGroupedPanel(
  //       'denoise_intensities',
  //       ['high_res', 'medium_res', 'low_res'],
  //       INTENSITIES_CHUNK_SIZE
  //     ),
  //   [filter.parameters?.denoise_intensities, onFloatValueChange, onGroupFloatValueChange]
  // );

  return (
    <div
      data-testid="filter-parametrization"
      className={`filter-parametrization ${isFineTunedProcessingOngoing ? 'processing' : ''}`}
    >
      <h3>
        <FormattedMessage id="tools.processing.finetuning.title" />
      </h3>
      <div>
        <h4>
          <FormattedMessage id={`tools.processing.finetuning.filter.grey_mapping.title`} />
        </h4>

        <div className="parameter-unit">
          <ValueSlider
            value={FILTER_PARAMETERS_MAPPINGS.general_steepness.toDisplay(
              filter.parameters.general_steepness
            )}
            reference={FILTER_PARAMETERS_MAPPINGS.general_steepness.toDisplay(
              referenceFilter.general_steepness
            )}
            name="general_steepness"
            {...FILTERS_PARAMETERS.general_steepness}
            onChange={onFloatValueChange}
            disabled={isFineTunedProcessingOngoing}
          />
        </div>
        <div className="parameter-unit">
          <ValueSlider
            value={FILTER_PARAMETERS_MAPPINGS.lowLatitudeCompression.toDisplay(
              filter.parameters.lowLatitudeCompression ?? 1
            )}
            reference={FILTER_PARAMETERS_MAPPINGS.lowLatitudeCompression.toDisplay(
              referenceFilter.lowLatitudeCompression ?? 1
            )}
            name="lowLatitudeCompression"
            {...FILTERS_PARAMETERS.lowLatitudeCompression}
            onChange={onFloatValueChange}
            disabled={isFineTunedProcessingOngoing}
          />
        </div>
        <div className="parameter-unit">
          <ValueSlider
            value={FILTER_PARAMETERS_MAPPINGS.black_relative_steepness.toDisplay(
              filter.parameters.black_relative_steepness
            )}
            reference={FILTER_PARAMETERS_MAPPINGS.black_relative_steepness.toDisplay(
              referenceFilter.black_relative_steepness
            )}
            name="black_relative_steepness"
            {...FILTERS_PARAMETERS.black_relative_steepness}
            onChange={onFloatValueChange}
            disabled={isFineTunedProcessingOngoing}
          />
        </div>
        {/* <div className="parameter-unit">
          <ValueSlider
            value={filter.parameters.white_relative_steepness}
            reference={referenceFilter.white_relative_steepness}
            name="white_relative_steepness"
            {...FILTERS_PARAMETERS.white_relative_steepness}
            onChange={onFloatValueChange}
          />
        </div> */}
      </div>
      <div>
        <h4>
          <FormattedMessage id={`tools.processing.finetuning.filter.sharpening.title`} />
        </h4>
        <div className="parameter-unit">
          <Accordion exclusive={false} panels={intensitiesPanels} />
        </div>
      </div>
      {/* <div>
        <h4>
          <FormattedMessage id={`tools.processing.finetuning.filter.denoise.title`} />
        </h4>
        <div className="parameter-unit">
          <Accordion exclusive={false} panels={denoisePanels} />
        </div>
      </div> */}
      <div>
        <h4>
          <FormattedMessage id={`tools.processing.finetuning.filter.viewport.title`} />
        </h4>
        <div className="parameter-unit">
          <ValueSlider
            value={FILTER_PARAMETERS_MAPPINGS.centering_quantile.toDisplay(
              filter.parameters.centering_quantile
            )}
            reference={FILTER_PARAMETERS_MAPPINGS.centering_quantile.toDisplay(
              referenceFilter.centering_quantile
            )}
            name="centering_quantile"
            {...FILTERS_PARAMETERS.centering_quantile}
            onChange={onFloatValueChange}
            disabled={isFineTunedProcessingOngoing}
          />
        </div>
        <div className="parameter-unit">
          <ValueSlider
            value={
              FILTER_PARAMETERS_MAPPINGS.gamma?.toDisplay(filter.parameters.gamma ?? 1) ??
              filter.parameters.gamma ??
              1
            }
            reference={
              FILTER_PARAMETERS_MAPPINGS.gamma?.toDisplay(referenceFilter.gamma ?? 1) ??
              referenceFilter.gamma ??
              1
            }
            name="gamma"
            {...FILTERS_PARAMETERS.gamma}
            onChange={onFloatValueChange}
            disabled={isFineTunedProcessingOngoing}
          />
        </div>
        {/* <div className="parameter-unit">
          <Checkbox
            label="need_log_rescale"
            name="need_log_rescale"
            checked={filter.parameters.need_log_rescale ?? false}
            onChange={onBooleanValueChange}
            disabled={isFineTunedProcessingOngoing}
          />
        </div>
        <div className="parameter-unit">
          <Checkbox
            label="late_log_rescale"
            name="late_log_rescale"
            checked={filter.parameters.late_log_rescale ?? false}
            onChange={onBooleanValueChange}
          />
        </div> */}
      </div>
      <div>
        <ImageHistogramDisplay
          {...imageForHist}
          min={0}
          max={1 << 12}
          style={{ width: '100%', height: 100 }}
        />
      </div>
      <div>
        <Input
          label={
            <label htmlFor="finetuning.filter_name.label" className="ui label">
              <FormattedMessage id={'tools.processing.finetuning.filter_name.label'} />
            </label>
          }
          id="finetuning.filter_name.label"
          value={filter?.name ?? ''}
          onChange={(e, { value }) => onChange({ ...filter, name: value })}
        />
      </div>
      <div className="actions-group">
        <Button
          inverted
          onClick={() => onCancel(filter)}
          icon="cancel"
          aria-label={intl.formatMessage({ id: 'general.cancel' })}
          title={intl.formatMessage({ id: 'general.cancel' })}
        />
        {filter?.id ? (
          <>
            <Button
              inverted
              color="green"
              icon="save"
              onClick={() => onConfirm(filter)}
              aria-label={intl.formatMessage({ id: 'general.edit' })}
              title={intl.formatMessage({ id: 'general.edit' })}
            />
            <ConfirmWithIntl
              open={isFilterDeletionOpen}
              data-testid="filter-parametrization-confirm"
              onConfirm={() => onDelete(filter)}
              onCancel={() => setIsFilterDeletionOpen(false)}
              onClose={() => setIsFilterDeletionOpen(false)}
              onOpen={() => setIsFilterDeletionOpen(true)}
              trigger={
                <Button
                  inverted
                  color="red"
                  icon="trash"
                  aria-label={intl.formatMessage({ id: 'general.delete' })}
                  title={intl.formatMessage({ id: 'general.delete' })}
                />
              }
              contentId={'tools.processing.finetuning.delete_confirm.content'}
              confirmId={'general.confirm'}
              cancelId={'general.cancel'}
              dangerous
            />
          </>
        ) : (
          <Button
            inverted
            color="green"
            icon="save"
            onClick={() => onConfirm(filter)}
            aria-label={intl.formatMessage({ id: 'general.save' })}
          />
        )}
      </div>

      {isFineTunedProcessingOngoing && (
        <Label corner="left" color="black">
          <Icon name="cog" loading />
        </Label>
      )}
    </div>
  );
});
